import React, { useState, useEffect, useRef } from 'react';
import { ShoppingBag, Tag, Users, Headphones, Store, ShoppingCart } from 'lucide-react';
import Navbar from './ui/navbar';
import './DualityLanding.css';

interface DualityLandingProps {
  onSellerSelect: () => void;
  onCustomerSelect: () => void;
}

const DualityLanding: React.FC<DualityLandingProps> = ({ onSellerSelect, onCustomerSelect }) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isHoveringCenter, setIsHoveringCenter] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [mergeState, setMergeState] = useState<'none' | 'seller' | 'customer'>('none');
  const cursorRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sellerBubbleRef = useRef<HTMLDivElement>(null);
  const customerBubbleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });

      // Check distance between cursor and bubbles for merge animation
      if (showChoices) {
        if (sellerBubbleRef.current) {
          const rect = sellerBubbleRef.current.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const distance = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));

          if (distance < 100) {
            setMergeState('seller');
          } else if (mergeState === 'seller') {
            setMergeState('none');
          }
        }

        if (customerBubbleRef.current) {
          const rect = customerBubbleRef.current.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const distance = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));

          if (distance < 100) {
            setMergeState('customer');
          } else if (mergeState === 'customer') {
            setMergeState('none');
          }
        }
      }
    };

    // Lock scroll on mount
    document.body.classList.add('scroll-locked');

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      // Unlock scroll on unmount
      document.body.classList.remove('scroll-locked');
      // Clear timeout on unmount
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [showChoices, mergeState]);

  const handleCenterHover = (isHovering: boolean) => {
    setIsHoveringCenter(isHovering);

    if (isHovering) {
      // Clear any existing timeout when hovering
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setShowChoices(true);
    } else {
      // When mouse leaves, set a 5-second timeout before hiding bubbles
      hideTimeoutRef.current = setTimeout(() => {
        setShowChoices(false);
      }, 5000);
    }
  };

  const handleChoice = (choice: 'seller' | 'customer') => {
    // Unlock scroll when user makes a choice
    document.body.classList.remove('scroll-locked');

    if (choice === 'seller') {
      onSellerSelect();
    } else {
      onCustomerSelect();
    }
  };

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "/create-space" },
    },
  };

  return (
    <div className="duality-landing">
      <div className="duality-content-wrapper">
        {/* Navigation */}
        <div className="fixed top-0 left-0 right-0 z-[10002] flex justify-center pt-6 px-4 pointer-events-none animate-navbar-float">
          <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[40px] px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
            <Navbar {...navbarConfig} />
          </div>
        </div>

        {/* Custom Cursor */}
        <div
          ref={cursorRef}
          className={`custom-cursor ${mergeState !== 'none' ? 'merging' : ''}`}
          style={{
            left: `${cursorPosition.x}px`,
            top: `${cursorPosition.y}px`,
          }}
        />

        {/* Background Card with Neon Granular Pattern */}
        <div className="neon-card">
          {/* Static Background Image */}
          <div className="static-background-wrapper">
            <img
              src="/home.png"
              alt="Home Background"
              className="static-background-image"
              loading="eager"
            />
          </div>

          {/* Fleait Logo */}
          <div className="fleait-logo">Fleait</div>

          {/* Central Interactive Circle */}
          <div className="center-container">
          <div
            className={`center-circle ${isHoveringCenter ? 'hovered' : ''}`}
            onMouseEnter={() => handleCenterHover(true)}
            onMouseLeave={() => handleCenterHover(false)}
          >
            <span className={`center-text ${showChoices ? 'fade-out' : ''}`}>Start Your Journey</span>
          </div>

          {/* Amoeba-like Choice Bubbles */}
          {showChoices && (
            <>
              <div className={`bubble-container seller-container ${mergeState === 'seller' ? 'merging' : ''}`}>
                <div
                  ref={sellerBubbleRef}
                  className={`choice-bubble seller-bubble ${mergeState === 'seller' ? 'merging' : ''}`}
                  onClick={() => handleChoice('seller')}
                >
                  <div className="bubble-content">
                    <Store size={40} strokeWidth={2} className="bubble-icon" />
                    <span className="bubble-text">Seller</span>
                  </div>
                </div>
              </div>
              <div className={`bubble-container customer-container ${mergeState === 'customer' ? 'merging' : ''}`}>
                <div
                  ref={customerBubbleRef}
                  className={`choice-bubble customer-bubble ${mergeState === 'customer' ? 'merging' : ''}`}
                  onClick={() => handleChoice('customer')}
                >
                  <div className="bubble-content">
                    <ShoppingCart size={40} strokeWidth={2} className="bubble-icon" />
                    <span className="bubble-text">Customer</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default DualityLanding;
